import { supabase, resolveFunctionError } from '@/lib/supabase';

export interface TrackedOrderItem {
  productName: string;
  productImage: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface TrackedPayment {
  method: string;
  status: string;
  amount: number;
  createdAt: string | null;
  installments: number | null;
  creditId: string;
  // Motivo del rechazo devuelto por Sistecrédito (vacío si no aplica).
  rejectionReason: string;
}

// Resumen del último intento de financiamiento con Sistecrédito, para que el
// cliente vea cuotas, ID de crédito y (si fue rechazado) el motivo.
export interface SistecreditoAttempt {
  status: string;
  installments: number | null;
  creditId: string;
  rejectionReason: string;
}

// Un intento individual dentro del HISTORIAL de Sistecrédito (cada fila de pago
// `sistecredito` del pedido).
export interface SistecreditoAttemptEntry {
  status: string;
  installments: number | null;
  creditId: string;
  rejectionReason: string;
  transactionRef: string;
  amount: number;
  createdAt: string | null;
}

export interface TrackedOrderStage {
  key: string;
  label: string;
}

export interface TrackedOrder {
  orderNumber: string;
  status: string;
  statusLabel: string;
  stageIndex: number;
  stages: TrackedOrderStage[];
  // El pedido está financiado con Sistecrédito y su solicitud sigue en curso
  // (aún sin aprobación). El cliente lo ve como "En financiamiento".
  financing: boolean;
  // El aliado RECHAZÓ/CANCELÓ el financiamiento: el pedido se auto-cancela y se
  // muestra un aviso claro con opción de reintentar con otro método de pago.
  financingDeclined: boolean;
  // Resumen del último intento de Sistecrédito (cuotas, crédito, motivo).
  sistecreditoAttempt: SistecreditoAttempt | null;
  // Historial COMPLETO de intentos (del más reciente al más antiguo).
  sistecreditoAttempts: SistecreditoAttemptEntry[];
  createdAt: string | null;
  subtotal: number;
  discountAmount: number;
  shippingCost: number;
  total: number;
  paymentMethod: string;
  production: boolean;
  isCancelled: boolean;
  recipientName: string;
  shippingCity: string;
  shippingDepartment: string;
  shippingAddress: string;
  items: TrackedOrderItem[];
  payments: TrackedPayment[];
}

export interface LookupResult {
  found: boolean;
  order: TrackedOrder | null;
}

// Consulta pública del estado de un pedido. Se hace a través del Edge Function
// `lookup-order` (service role) porque el cliente no tiene sesión: exige el
// número de pedido + un dato de contacto (celular, correo o documento).
export async function lookupOrder(orderNumber: string, contact: string): Promise<LookupResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }
  const { data, error } = await supabase.functions.invoke('lookup-order', {
    body: { orderNumber, contact },
  });
  if (error) {
    throw new Error(await resolveFunctionError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data as LookupResult;
}

// ¿Ya llegó la aprobación del financiamiento Sistecrédito? Se considera
// aprobado cuando existe un pago 'sistecredito' con estado 'approved' o cuando
// el pedido avanzó a un estado posterior a la aprobación. Solo en ese momento
// se debe vaciar del carrito los productos del pedido (paridad con ePayco).
export function isSistecreditoOrderApproved(order: TrackedOrder): boolean {
  if (order.financing || order.financingDeclined || order.isCancelled) return false;
  if (
    order.payments.some(
      (p) => p.method === 'sistecredito' && p.status === 'approved',
    )
  ) {
    return true;
  }
  return [
    'payment_confirmed',
    'listo_envio',
    'producido',
    'entregado',
    'closed',
    'para_produccion',
  ].includes(order.status);
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Pago contra entrega',
  transfer: 'Nequi / Transferencia',
  payment_link: 'ePayco (tarjeta / PSE / Nequi)',
  sistecredito: 'Sistecrédito (Compra a Cuotas)',
  card: 'Tarjeta',
  other: 'Otro',
};

export function paymentMethodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS[method] ?? 'No especificado';
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
};

export function paymentStatusLabel(status: string): string {
  return PAYMENT_STATUS_LABELS[status] ?? status;
}